/**
 * A greenhouse gas inventory.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { GHG_SCOPES, SCOPE3_CATEGORIES, type GhgLine, type GhgScope } from '@/domain/ghg'
import { readArray, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import { newId, type GhgSection } from '@/domain/section'
import GhgBlock from '@/sections/ghg/GhgBlock.vue'
import GhgInspector from '@/sections/ghg/GhgInspector.vue'
import type { SectionDefinition } from '@/sections/types'

function readScope(value: string): GhgScope {
  return (GHG_SCOPES as readonly string[]).includes(value) ? (value as GhgScope) : 'scope1'
}

/** A category, if it is one of the fifteen. Blank for anything else. */
function readCategory(value: string): string {
  return (SCOPE3_CATEGORIES as readonly string[]).includes(value) ? value : ''
}

function readLine(value: JsonValue): GhgLine {
  return {
    id: readString(value, 'id') || newId(),
    scope: readScope(readString(value, 'scope')),
    category: readCategory(readString(value, 'category')),
    label: readString(value, 'label'),
    value: readString(value, 'value'),
    comparative: readString(value, 'comparative'),
  }
}

/**
 * What a fresh inventory starts as.
 *
 * One line per scope, with **both** scope 2 bases present from the start.
 * Starting with only one would be starting with the commonest omission there
 * is, and somebody filling in what they were given rarely adds a row nobody
 * put there.
 */
function startingLines(): GhgLine[] {
  return [
    { id: newId(), scope: 'scope1', category: '', label: '', value: '', comparative: '' },
    { id: newId(), scope: 'scope2Location', category: '', label: '', value: '', comparative: '' },
    { id: newId(), scope: 'scope2Market', category: '', label: '', value: '', comparative: '' },
    { id: newId(), scope: 'scope3', category: '1', label: '', value: '', comparative: '' },
  ]
}

export const ghgSection: SectionDefinition<'ghg', GhgSection> = {
  kind: 'ghg',
  band: 'sustainability',

  icon: ['M4.5 11.5a2.6 2.6 0 0 1 0-5.2 3.4 3.4 0 0 1 6.5-1.1 2.7 2.7 0 0 1 .5 5.3z', 'M6 13.8h5'],

  create: (id, title) => ({
    id,
    kind: 'ghg',
    title,
    lines: startingLines(),
    unit: 'tCO2e',
    period: '',
    comparativePeriod: '',
    intensityLabel: '',
    intensityValue: '',
    intensityComparative: '',
    caption: '',
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'ghg',
    title,
    lines: readArray(stored, 'lines').map(readLine),
    unit: readString(stored, 'unit', 'tCO2e'),
    period: readString(stored, 'period'),
    comparativePeriod: readString(stored, 'comparativePeriod'),
    intensityLabel: readString(stored, 'intensityLabel'),
    intensityValue: readString(stored, 'intensityValue'),
    intensityComparative: readString(stored, 'intensityComparative'),
    caption: readString(stored, 'caption'),
  }),

  block: GhgBlock,
  inspector: GhgInspector,
}
